import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Play, Pause, Clock } from 'lucide-react';
import axios from '../utils/axiosConfig';
import { toast } from 'sonner';
import whistleSoccer from '../assets/sounds/whistle-soccer.mp3';

const API = '/api';

const SharedTimer = ({ sessaoId, duracaoMinutos }) => {
  // States
  const [timerStatus, setTimerStatus] = useState('NOT_STARTED');
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [totalSeconds, setTotalSeconds] = useState(null);
  
  const countdownRef = useRef(null);
  const pollingRef = useRef(null);

  // If no duration configured, don't render anything
  if (!duracaoMinutos) {
    return null;
  }

  // Initial fetch and WebSocket listener
  useEffect(() => {
    fetchTimerState();
    
    // Listen for WebSocket timer updates (instant sync)
    const handleTimerUpdate = (event) => {
      const { timer_status, timer_remaining_seconds } = event.detail;
      console.log('[SharedTimer] ⚡ WebSocket timer update:', timer_status, timer_remaining_seconds);
      
      // Clear countdown when receiving WebSocket update
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
    };
    
    window.addEventListener('timer_update_ws', handleTimerUpdate);
    
    // Reduced polling interval (only as backup if WebSocket fails)
    pollingRef.current = setInterval(fetchTimerState, 10000);
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      window.removeEventListener('timer_update_ws', handleTimerUpdate);
    };
  }, [sessaoId]);

  // Local countdown animation (only visual)
  useEffect(() => {
    // Clear any existing countdown
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Start countdown only if RUNNING and has remaining time
    if (timerStatus === 'RUNNING' && remainingSeconds > 0) {
      countdownRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            setTimerStatus('FINISHED');
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [timerStatus, remainingSeconds]);

  // Fetch timer state from backend
  const fetchTimerState = async () => {
    try {
      const response = await axios.get(`${API}/sessoes/${sessaoId}/timer`);
      const { timer_status, timer_remaining_seconds, timer_duracao_total_segundos } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      setTotalSeconds(timer_duracao_total_segundos || duracaoMinutos * 60);
    } catch (error) {
      console.error('Erro ao buscar timer:', error);
    }
  };

  // Start timer
  const handleStart = async () => {
    // NEW: Play whistle sound when starting
    try {
      const audio = new Audio(whistleSoccer);
      audio.volume = 0.5;
      audio.play().catch(err => console.error('Erro ao reproduzir áudio:', err));
    } catch (audioError) {
      console.error('Erro ao reproduzir áudio:', audioError);
    }
    
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/start`);
      const { timer_status, timer_remaining_seconds, timer_duracao_total_segundos } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      setTotalSeconds(timer_duracao_total_segundos);
      
      toast.success('Timer iniciado!');
    } catch (error) {
      console.error('Erro ao iniciar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar timer');
    }
  };

  // Pause timer
  const handlePause = async () => {
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/pause`);
      const { timer_status, timer_remaining_seconds } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      
      toast.success('Timer pausado!');
    } catch (error) {
      console.error('Erro ao pausar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao pausar timer');
    }
  };

  // Resume timer
  const handleResume = async () => {
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/resume`);
      const { timer_status, timer_remaining_seconds } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      
      toast.success('Timer retomado!');
    } catch (error) {
      console.error('Erro ao retomar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao retomar timer');
    }
  };

  // Format seconds to MM:SS
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) {
      if (totalSeconds) {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      }
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status text in Portuguese
  const getStatusText = () => {
    switch (timerStatus) {
      case 'NOT_STARTED': return 'Aguardando início';
      case 'RUNNING': return 'Em andamento';
      case 'PAUSED': return 'Pausado';
      case 'FINISHED': return 'Finalizado';
      default: return 'Aguardando início';
    }
  };

  // Get button config
  const getButtonConfig = () => {
    switch (timerStatus) {
      case 'NOT_STARTED':
        return {
          text: 'Iniciar',
          icon: Play,
          onClick: handleStart,
          disabled: false,
          className: 'bg-green-600 hover:bg-green-700'
        };
      case 'RUNNING':
        return {
          text: 'Pausar',
          icon: Pause,
          onClick: handlePause,
          disabled: false,
          className: 'bg-orange-600 hover:bg-orange-700'
        };
      case 'PAUSED':
        return {
          text: 'Continuar',
          icon: Play,
          onClick: handleResume,
          disabled: false,
          className: 'bg-green-600 hover:bg-green-700'
        };
      case 'FINISHED':
        return {
          text: 'Finalizado',
          icon: Clock,
          onClick: null,
          disabled: true,
          className: 'bg-gray-400 cursor-not-allowed'
        };
      default:
        return {
          text: 'Iniciar',
          icon: Play,
          onClick: handleStart,
          disabled: false,
          className: 'bg-green-600 hover:bg-green-700'
        };
    }
  };

  const buttonConfig = getButtonConfig();
  const ButtonIcon = buttonConfig.icon;

  return (
    <Card className="bg-[#0f0f0f] border-gray-800">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2 text-lg">
          <Clock className="w-5 h-5 text-[#CF0D1D]" />
          Cronômetro da Estação
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-4xl font-bold font-mono text-white">
              {formatTime(remainingSeconds)}
            </div>
            <p className="text-sm text-gray-400 mt-1">
              {getStatusText()}
            </p>
          </div>
          <Button
            onClick={buttonConfig.onClick}
            disabled={buttonConfig.disabled}
            className={buttonConfig.className}
          >
            <ButtonIcon className="w-4 h-4 mr-2" />
            {buttonConfig.text}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SharedTimer;
